// OpenSource.tsx

import React from 'react';
import './OpenSource.css';
import { FaStar, FaGithub, FaCode } from 'react-icons/fa';

interface OssProject {
  name: string;
  stars: number;
  language: string;
  description: string;
  repoUrl: string;
}

const TIRITH_REPO = 'https://github.com/sheeki03/tirith';
const TIRITH_SITE = 'https://tirith.sh';

const TIRITH_STATS: string[] = [
  '240+ detection rules',
  '35 categories',
  '80+ releases',
  'AGPL-3.0',
  'No telemetry',
];

const TIRITH_HIGHLIGHTS: string[] = [
  'Merged into Nous Research Hermes Agent (~226k stars), March 2026',
  'Vercel Open Source Program, Spring 2026',
  'OTF Internet Freedom Fund proposal invited',
];

const OSS_PROJECTS: OssProject[] = [
  {
    name: 'Few-Word',
    stars: 38,
    language: 'Python',
    description:
      'Claude Code plugin that offloads large outputs to the filesystem — ~82% context-token savings.',
    repoUrl: 'https://github.com/sheeki03/Few-Word',
  },
  {
    name: 'analystOS',
    stars: 23,
    language: 'Python',
    description:
      'AI research platform for stocks and crypto: multi-agent research pipelines, web UI + Notion automation, RAG over docs and scraped URLs, 50+ models via OpenRouter.',
    repoUrl: 'https://github.com/sheeki03/analystOS',
  },
  {
    name: 'Schrute',
    stars: 14,
    language: 'TypeScript',
    description:
      'Universal self-learning browser agent: UI automation on the first run, API-level replay by the hundredth.',
    repoUrl: 'https://github.com/sheeki03/schrute',
  },
  {
    name: 'logbook',
    stars: 3,
    language: 'Rust',
    description:
      'Local-first black-box flight recorder for AI coding agents — prompts, tool calls, commands, and file changes on one redacted, replayable local timeline.',
    repoUrl: 'https://github.com/sheeki03/logbook',
  },
];

const OpenSource: React.FC = () => {
  return (
    <article className="opensource-container">
      <header className="opensource-header">
        <h1 className="opensource-title">Open Source</h1>
        <p className="opensource-subtitle">
          Tools built and maintained by Shivom Sharma — headlined by tirith.
        </p>
      </header>

      {/* Featured project: tirith */}
      <section className="oss-hero" aria-label="Featured open source project: tirith">
        <div className="oss-hero-top">
          <div className="oss-hero-heading">
            <h2 className="oss-hero-name">tirith</h2>
            <p className="oss-hero-role">Creator &amp; Maintainer</p>
          </div>
          <div className="oss-hero-badges">
            <span className="oss-star-badge">
              <FaStar aria-hidden="true" /> 2,683
              <span className="visually-hidden">stars on GitHub</span>
            </span>
            <span className="oss-lang-chip">Rust</span>
          </div>
        </div>

        <p className="oss-hero-desc">
          Pre-execution control layer that inspects what terminals and AI agents are about to run
          and blocks it before anything executes — homograph URLs, pipe-to-shell, ANSI injection,
          obfuscated payloads, data exfiltration, and malicious AI skills/configs. Ships 240+
          detection rules across 35 categories, backed by a signed local threat database, with no
          telemetry.
        </p>

        <ul className="oss-stat-row" aria-label="tirith key stats">
          {TIRITH_STATS.map((stat) => (
            <li key={stat} className="oss-stat-chip">{stat}</li>
          ))}
        </ul>

        <ul className="oss-highlight-strip" aria-label="tirith highlights">
          {TIRITH_HIGHLIGHTS.map((highlight) => (
            <li key={highlight} className="oss-highlight">{highlight}</li>
          ))}
        </ul>

        <div className="oss-hero-actions">
          <a
            className="oss-btn oss-btn--primary"
            href={TIRITH_REPO}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="View tirith on GitHub (opens in a new tab)"
          >
            <FaGithub aria-hidden="true" /> View on GitHub
          </a>
          <a
            className="oss-btn oss-btn--ghost"
            href={TIRITH_SITE}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Visit tirith.sh (opens in a new tab)"
          >
            <FaCode aria-hidden="true" /> tirith.sh
          </a>
        </div>
      </section>

      {/* Other open source projects */}
      <section className="oss-grid" aria-label="More open source projects">
        {OSS_PROJECTS.map((project) => (
          <article key={project.name} className="oss-card">
            <div className="oss-card-head">
              <h3 className="oss-card-name">{project.name}</h3>
              <span className="oss-star-badge">
                <FaStar aria-hidden="true" /> {project.stars}
                <span className="visually-hidden">stars on GitHub</span>
              </span>
            </div>
            <span className="oss-lang-chip">{project.language}</span>
            <p className="oss-card-desc">{project.description}</p>
            <a
              className="oss-card-link"
              href={project.repoUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`View ${project.name} on GitHub (opens in a new tab)`}
            >
              <FaGithub aria-hidden="true" /> GitHub
            </a>
          </article>
        ))}
      </section>

      {/* Footer card */}
      <section className="oss-more">
        <a
          className="oss-more-link"
          href="https://github.com/sheeki03"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="View Shivom Sharma's GitHub profile (opens in a new tab)"
        >
          More on GitHub
        </a>
        <p className="oss-more-note">44 repos and counting.</p>
      </section>
    </article>
  );
};

export default OpenSource;
